import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DragDropContext } from 'react-beautiful-dnd';
import { propTypes as searchItemPropTypes } from 'shared/proptypes/SearchItem';
import { createFulltextItem } from 'shared/lib/searchHelpers';
import { createMoneyFormatter } from 'shared/lib/formatters';
import { DEFAULT_CURRENCY } from 'shared/constants';
import CustomButton from 'shared/components/buttons/Custom';
import AnySuggestion from 'userportal/components/AnySuggestion';
import styles from './Fulltext.module.scss';


const Fulltext = ({
  items,
  loading,
  onSuggestionSelect,
  onChangeLevel,
  onRemoveSuggestion,
  onRemoveLastRequiredSuggestion,
  onRemoveLastIgnoredSuggestion,
  onSearchSubmit,
  onSuggestionsContainerDisplay,
  onRemoveIgnored,
  onToggleItemRequired,
  onToggleItemMode,
  onDragDrop,
  disabled,
  currency
}) => {
  const { i18n, t } = useTranslation('userportal');
  const formatter = createMoneyFormatter(i18n.language);
  const [expanded, setExpanded] = useState(null);
  const toggleExpanded = (event) => {
    event.preventDefault();
    setExpanded(prev => !prev);
    onRemoveIgnored();
  };

  const [requiredFulltextValue, setRequiredFulltextValue] = useState('');
  const [ignoredFulltextValue, setIgnoredFulltextValue] = useState('');

  const onRequiredChange = value => setRequiredFulltextValue(value);
  const onIgnoredChange = value => setIgnoredFulltextValue(value);

  const onRequiredSelect = (item) => {
    onSuggestionSelect(item);
    setRequiredFulltextValue('');
  };

  const onIgnoredSelect = (item) => {
    onSuggestionSelect({ ...item, not: true });
    setIgnoredFulltextValue('');
  };

  const onSubmit = (event) => {
    event.persist();
    let item;
    if (requiredFulltextValue.trim().length > 0) {
      item = createFulltextItem(t, requiredFulltextValue);
      onRequiredSelect(item);
    } else if (ignoredFulltextValue.trim().length > 0) {
      item = createFulltextItem(t, ignoredFulltextValue);
      onIgnoredSelect(item);
    }
    onSearchSubmit(event, item);
    setRequiredFulltextValue('');
    setIgnoredFulltextValue('');
  };

  const translateItem = (item) => {
    switch (item.type) {
      case 'rate':
        return {
          ...item,
          title: t(item.not ? 'common:suggestion.typeRateInverse' : 'common:suggestion.typeRate', { value: formatter(item.slug, currency) })
        };
      case 'homeoffice':
        if (~~item.slug === 0) {
          return {
            ...item,
            title: t('common:suggestion.typeHomeofficeNa')
          };
        }

        return {
          ...item,
          title: `${t(item.not ? 'common:suggestion.typeHomeofficeInverse' : 'common:suggestion.typeHomeoffice')} ${t(
            'common:suggestion.typeHomeofficeValue',
            { postProcess: 'interval', count: ~~item.slug }
          )}`
        };
      case 'text':
        return {
          ...item,
          title: t(item.not ? 'common:suggestion.typeTextInverse' : 'common:suggestion.typeText', { value: item.slug })
        };
      default:
        return item;
    }
  };

  const selected = items.map(item => translateItem(item));
  const required = selected.filter(({ not }) => !not);
  const ignored = selected.filter(({ not }) => not);

  useEffect(() => {
    if (ignored.length > 0 && expanded === null) {
      setExpanded(true);
    }
  }, [ignored]);

  return (
    <div className={styles.fulltext}>
      {items.length > 0 ? (
        <>
          <h5>{t('motto')}</h5>
          <h6>{t('submotto')}</h6>
        </>
      ) : (
        <>
          <h1>{t('motto')}</h1>
          <h2>{t('submotto')}</h2>
        </>
      )}
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="col-xl-11 col-lg-11 col-md-10 col-sm-11 col-xs-12">
            <DragDropContext onDragEnd={onDragDrop}>
              <AnySuggestion
                id="required"
                externalValue={requiredFulltextValue}
                disabled={disabled}
                selected={required}
                loading={loading}
                onChange={onRequiredChange}
                onToggleRequired={onToggleItemRequired}
                onToggleMode={onToggleItemMode}
                onChangeLevel={onChangeLevel}
                submitDisabled={items.length === 0}
                onSelect={onRequiredSelect}
                onRemove={onRemoveSuggestion}
                onRemoveLastSelected={onRemoveLastRequiredSuggestion}
                onSuggestionsContainerDisplay={onSuggestionsContainerDisplay}
                currency={currency}
              />
              {expanded ? (
                <div className={styles.ignoredTitle}>
                  <span>
                    <i className="la la-chevron-circle-right" />
                    {t('fulltext.openIgnored')}:
                  </span>
                  <CustomButton
                    color="white"
                    iconClassName="la la-times-circle"
                    onClick={toggleExpanded}
                    className={styles.toggle}
                  >
                    {t('common:linkButton.cancel')}
                  </CustomButton>
                </div>
              ) : (
                <div className={styles.toggleBar}>
                  <CustomButton
                    color="white"
                    iconClassName="la la-chevron-circle-down"
                    onClick={toggleExpanded}
                  >
                    {t('fulltext.openIgnored')}
                  </CustomButton>
                  <a href="https://blog.skilleto.cz/vyhledavani-pracovnich-pozic/" target="_blank" rel="noopener noreferrer">
                    <i className="la la-info-circle" />
                    {t('fulltext.hint')}
                  </a>
                </div>
              )}
              {expanded && (
                <AnySuggestion
                  isInverseSearch
                  id="ignored"
                  externalValue={ignoredFulltextValue}
                  disabled={disabled}
                  selected={ignored}
                  loading={loading}
                  onChange={onIgnoredChange}
                  onToggleMode={onToggleItemMode}
                  onChangeLevel={onChangeLevel}
                  submitDisabled={items.length === 0}
                  onSelect={onIgnoredSelect}
                  onRemove={onRemoveSuggestion}
                  onRemoveLastSelected={onRemoveLastIgnoredSuggestion}
                  onSuggestionsContainerDisplay={onSuggestionsContainerDisplay}
                  currency={currency}
                />
              )}
            </DragDropContext>
          </div>
          <div className="col-xl-1 col-lg-1 col-md-2 col-sm-1 col-xs-12">
            <button
              className={styles.submit}
              type="submit"
              disabled={loading}
            >
              <i className="la la-search" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

Fulltext.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(searchItemPropTypes)),
  loading: PropTypes.bool,
  onSuggestionSelect: PropTypes.func,
  onChangeLevel: PropTypes.func,
  onRemoveSuggestion: PropTypes.func,
  onRemoveLastRequiredSuggestion: PropTypes.func,
  onRemoveLastIgnoredSuggestion: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  onDragDrop: PropTypes.func,
  onRemoveIgnored: PropTypes.func,
  onToggleItemRequired: PropTypes.func,
  onToggleItemMode: PropTypes.func,
  disabled: PropTypes.bool,
  currency: PropTypes.string
};

Fulltext.defaultProps = {
  items: [],
  loading: false,
  onSuggestionSelect() {},
  onChangeLevel() {},
  onRemoveSuggestion() {},
  onRemoveLastRequiredSuggestion() {},
  onRemoveLastIgnoredSuggestion() {},
  onSearchSubmit() {},
  onDragDrop() {},
  onRemoveIgnored() {},
  onToggleItemRequired() {},
  onToggleItemMode() {},
  disabled: false,
  currency: DEFAULT_CURRENCY
};

export default Fulltext;
